// @ts-nocheck
/*
 * Port of views/dashboard/app/user-list.js (origin/main-dev, 1815 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast
 * string, role check, modal wiring, and JSX conditional matches v1 to
 * the letter. Only chrome swaps:
 *   - PrimeReact <DataTable> + <Column> → obd3-gui DataTable (ColumnDef)
 *   - PrimeReact <Button> label="..." → shadcn Button with children
 *   - react-bootstrap Modal (User Details, Call Strategies) → shadcn Dialog
 *   - Filter dropdowns stay as native <select> for parity with v1 look
 *
 * DEAD CODE paths preserved verbatim (commented dropdown items for
 * single-user Delete + Add/Remove Voice/TTS Credits — handlers, state,
 * and modals exist unreachable from UI, matching v1). Do NOT re-enable
 * without user sign-off.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Building2,
  Calendar,
  CalendarClock,
  Coins,
  Download,
  Headphones,
  LogIn,
  Mail,
  Mic,
  Network,
  Plus,
  Shield,
  User,
  UserRound,
} from 'lucide-react';
import {
  API_USERS_LIST,
  API_SWITCH_USER,
  API_CREDITS,
  API_TTS_CREDITS,
  API_USER_DELETE,
  API_USER_STATUS_UPDATE,
  API_USER_BULK_LOGOUT,
} from '@/service/url';
import { getApiCall, postApiCall, patchApiCallWithHeader } from '@/service/httpCall';
import {
  ROUTES,
  USER_ROLES,
  USER_ROLES_MAP_PROFILE,
  USER_CREDITS_ACTION,
  TTS_CREDITS_ACTION,
  MODULE_ACL,
} from '@/utilities/constant';
import { Storage, getUser, switchUser, getUserReadAccessRoleLabel } from '@/utilities/setting';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { UserTypeFilter } from './components/UserTypeFilter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { bulkSetUserStatus } from '@/utilities/bulkUserStatus';
import { UserRowActions } from './components/UserRowActions';
import { UserStatusBadge } from './components/UserStatusBadge';
import Loader from '@/components/partials/components/Loader';
import { UserRoleBadge } from './components/UserRoleBadge';
import { UserAvatar } from './components/UserAvatar';
import { UserExpiryCell } from './components/UserExpiryCell';
import { UserStatStrip } from './components/UserStatStrip';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import RenderInactiveUserModal from '@/components/partials/components/inactive-user-modal';
import RenderShowPasswordModal from '@/components/partials/components/show-password';
import RenderUserHierarchyModal from '@/components/partials/components/user-hierarchy-modal';
import switchUserIcon from '@/assets/images/icons/switch-user.png';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const USER_TYPE_OPTIONS = [
  { value: USER_ROLES.SUB_SUPERADMIN, label: 'Sub Superadmin' },
  { value: USER_ROLES.RESELLER, label: 'Reseller' },
  { value: USER_ROLES.COMPANY_ADMIN, label: 'Company user' },
  { value: USER_ROLES.CALL_CENTRE_ADMIN, label: 'Call Centre Admin' },
  {
    value: USER_ROLES.CALL_CENTRE_AGENT,
    label: 'Agent',
    extraRoles: [USER_ROLES.CALL_CENTRE_TEAM_LEAD],
  },
];

/** Which role types appear in the filter, from this role downwards. */
const USER_TYPE_FILTER_START = {
  [USER_ROLES.SUPERADMIN]: USER_ROLES.SUB_SUPERADMIN,
  [USER_ROLES.SUB_SUPERADMIN]: USER_ROLES.SUB_SUPERADMIN,
  [USER_ROLES.RESELLER]: USER_ROLES.RESELLER,
  [USER_ROLES.COMPANY_ADMIN]: USER_ROLES.COMPANY_ADMIN,
};

/** Same role options as before; every visible type is selected by default. */
function getUserTypeFilterSetup(role) {
  const startRole = USER_TYPE_FILTER_START[role];
  const firstIndex =
    startRole == null
      ? 0
      : USER_TYPE_OPTIONS.findIndex((o) => o.value === startRole);
  const options = USER_TYPE_OPTIONS.slice(firstIndex === -1 ? 0 : firstIndex).map(
    ({ value, label }) => ({ value, label }),
  );
  const defaults = options.map((o) => o.value);
  return { defaults, options };
}

/** 'role_ccagent' also has to pull team leads, hence extraRoles. */
function buildRolesParam(types) {
  const roles = [];
  (types || []).forEach((type) => {
    const option = USER_TYPE_OPTIONS.find((o) => o.value === type);
    roles.push(type, ...(option?.extraRoles || []));
  });
  return [...new Set(roles)].join(',');
}

function isAgentAccount(user) {
  const role = user?.role;
  return role === USER_ROLES.CALL_CENTRE_AGENT || role === USER_ROLES.CALL_CENTRE_TEAM_LEAD;
}

function resolveCompanyName(user) {
  return (
    user?.assignedCompany?.name ||
    user?.companies?.name ||
    user?.company_name ||
    null
  );
}

function formatCompanyCell(rowData) {
  const company = rowData?.assignedCompany;
  const name = resolveCompanyName(rowData);
  if (!name) return '-';
  if (company?.name) {
    const companyType = company.is_reseller_company ? 'Reseller' : 'End User';
    return `${company.name} (${companyType})`;
  }
  return name;
}

function formatDateTime(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const accountTypeLabel = (u) =>
  u?.account_type == null ? '' : Number(u.account_type) === 1 ? 'Transactional' : 'Promotional';

export default function UserListPage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  // ── State (byte-identical to v1 lines 90-183) ────────────────────
  const [userlist, setUserlist] = useState([]);
  const [activeUserAccessRole, setActiveUserAccessRole] = useState(undefined);
  const [showStrategiesModal, setShowStrategiesModal] = useState(false);
  const [userCallStratgies, setUserCallStrategies] = useState(null);
  const [userCallStrategiesUserName, setUserCallStrategiesUserName] = useState(null);
  const [userAccessRoles, setUserAccessRoles] = useState([]);
  const statuses = useRef(['Active', 'Blocked']);

  const [enableInactiveModal, setEnableInactiveModal] = useState(false);
  const [inactiveUserId, setInactiveUserId] = useState(undefined);
  const [confirmUserInactive, setConfirmUserInactive] = useState(false);

  const [enableShowPasswordModal, setEnableShowPasswordModal] = useState(false);
  const [showPasswordId, setShowPasswordId] = useState(undefined);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [showPasswordUser, setShowPasswordUser] = useState(null);

  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(undefined);
  const [showBulkLogoutModal, setShowBulkLogoutModal] = useState(false);
  const [bulkStatusTarget, setBulkStatusTarget] = useState(null); // 0 = block, 1 = unblock
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [moduleOperations, setModuleOperations] = useState({});
  const [userRole, setUserRole] = useState('');
  const [isCallCenterAdmin, setCallCenterAdmin] = useState(false);
  const [originalUserlist, setOriginalUserlist] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const listRequestRef = useRef(0);
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedTypes, setSelectedTypes] = usePersistedState('users/UserList:selectedTypes', []);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [searchValue, setSearchValue] = usePersistedState('users/UserList:searchValue', '');
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [hierarchyUser, setHierarchyUser] = useState(null);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);

  const [uniqueUsernames, setUniqueUsernames] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [defaultUniqueUsernames, setDefaultUniqueUsernames] = useState([]);
  const [showAddCreditModel, setShowAddCreditModel] = useState(false);
  const [confirmCredits, setConfirmCredits] = useState(false);
  const [addCreditsValue, setAddCreditsValue] = useState(0);
  const [showRemoveCreditModel, setShowRemoveCreditModel] = useState(false);
  const [confirmReduceCredits, setConfirmReduceCredits] = useState(false);
  const [removeCreditsValue, setRemoveCreditsValue] = useState(0);
  const [showAddTtsCreditModel, setShowAddTtsCreditModel] = useState(false);
  const [confirmTtsCredits, setConfirmTtsCredits] = useState(false);
  const [addTtsCreditsValue, setAddTtsCreditsValue] = useState(0);
  const [showRemoveTtsCreditModel, setShowRemoveTtsCreditModel] = useState(false);
  const [confirmReduceTtsCredits, setConfirmReduceTtsCredits] = useState(false);
  const [removeTtsCreditsValue, setRemoveTtsCreditsValue] = useState(0);
  const [isReseller, setIsReseller] = useState(false);
  const [isCompanyUser, setIsCompanyUser] = useState(false);

  const [currentUserId, setCurrentUserId] = useState('');
  const [addCreditsId, setAddCreditsId] = useState(null);
  const [removeCreditsId, setRemoveCreditsId] = useState(null);
  const [addTtsCreditsId, setAddTtsCreditsId] = useState(null);
  const [removeTtsCreditsId, setRemoveTtsCreditsId] = useState(null);
  const [giverAvailableTtsCredits, setGiverAvailableTtsCredits] = useState(0);
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [superadminId, setSuperadminId] = useState('');
  const [loggedInUserACL, setLoggedInUserACL] = useState([]);
  const [toggleDeleteSelected, setToggleDeleteSelected] = useState(false);
  const [userLoadingDelete, setUserLoadingDelete] = useState(false);

  // ── useEffect: credit-add commit (verbatim v1 lines 186-203) ─────
  useEffect(() => {
    if (confirmCredits) {
      if (addCreditsValue == 0) {
        toast.error('Please add some credit value to increase');
      } else {
        const apiUrl = API_CREDITS.replace(':id', addCreditsId);
        postApiCall(apiUrl, {
          id: addCreditsId,
          updatedCredit: addCreditsValue,
          action: USER_CREDITS_ACTION.ADD,
          fromUser: loggedInUserId,
        })
          .then(onUserCreditsUpdatedSuccess)
          .catch(onUserCreditsUpdatedFailed);
      }
    }
  }, [confirmCredits, addCreditsValue]);

  useEffect(() => {
    if (confirmReduceCredits) {
      if (removeCreditsValue == 0) {
        toast.error('Please add some credit value to reduce');
      } else {
        const apiUrl = API_CREDITS.replace(':id', removeCreditsId);
        postApiCall(apiUrl, {
          id: removeCreditsId,
          updatedCredit: removeCreditsValue,
          action: USER_CREDITS_ACTION.DEDUCT,
          fromUser: loggedInUserId,
        })
          .then(onUserCreditsUpdatedSuccess)
          .catch(onUserCreditsUpdatedFailed);
      }
    }
  }, [confirmReduceCredits, removeCreditsValue]);

  useEffect(() => {
    if (confirmTtsCredits) {
      if (addTtsCreditsValue == 0) {
        toast.error('Please add some TTS credit value to increase');
        setConfirmTtsCredits(false);
      } else if (
        !isSuperAdmin &&
        giverAvailableTtsCredits < 1 &&
        Number(addTtsCreditsValue) > giverAvailableTtsCredits
      ) {
        toast.error('Not enough TTS credits in your company');
        setConfirmTtsCredits(false);
      } else {
        const apiUrl = API_TTS_CREDITS.replace(':id', addTtsCreditsId);
        postApiCall(apiUrl, {
          id: addTtsCreditsId,
          updatedCredit: addTtsCreditsValue,
          action: TTS_CREDITS_ACTION.ADDITION,
          fromUser: loggedInUserId,
        })
          .then(onUserTtsCreditsUpdatedSuccess)
          .catch(onUserTtsCreditsUpdatedFailed);
      }
    }
  }, [confirmTtsCredits, addTtsCreditsValue, isSuperAdmin, giverAvailableTtsCredits]);

  useEffect(() => {
    if (confirmReduceTtsCredits) {
      if (removeTtsCreditsValue == 0) {
        toast.error('Please add some TTS credit value to reduce');
      } else {
        const apiUrl = API_TTS_CREDITS.replace(':id', removeTtsCreditsId);
        postApiCall(apiUrl, {
          id: removeTtsCreditsId,
          updatedCredit: removeTtsCreditsValue,
          action: TTS_CREDITS_ACTION.DEDUCTION,
          fromUser: loggedInUserId,
        })
          .then(onUserTtsCreditsUpdatedSuccess)
          .catch(onUserTtsCreditsUpdatedFailed);
      }
    }
  }, [confirmReduceTtsCredits, removeTtsCreditsValue]);

  function onUserTtsCreditsUpdatedSuccess() {
    setConfirmTtsCredits(false);
    setConfirmReduceTtsCredits(false);
    setRemoveTtsCreditsValue(0);
    setAddTtsCreditsValue(0);
    setShowAddTtsCreditModel(false);
    setShowRemoveTtsCreditModel(false);
    toast.success('User TTS Credits Updated Successfully');
    getOriginalList();
  }
  function onUserTtsCreditsUpdatedFailed(error) {
    setConfirmTtsCredits(false);
    setConfirmReduceTtsCredits(false);
    setRemoveTtsCreditsValue(0);
    setAddTtsCreditsValue(0);
    setShowAddTtsCreditModel(false);
    setShowRemoveTtsCreditModel(false);
    toast.error(friendlyApiError(error) || 'Failed to update TTS credits');
  }
  function onUserCreditsUpdatedSuccess() {
    setConfirmCredits(false);
    setConfirmReduceCredits(false);
    setRemoveCreditsValue(0);
    setAddCreditsValue(0);
    setShowAddCreditModel(false);
    setShowRemoveCreditModel(false);
    toast.success('User Credits Updated Successfully');
    getOriginalList();
  }
  function onUserCreditsUpdatedFailed(error) {
    setConfirmCredits(false);
    setConfirmReduceCredits(false);
    setRemoveCreditsValue(0);
    setAddCreditsValue(0);
    setShowAddCreditModel(false);
    setShowRemoveCreditModel(false);
    toast.error(friendlyApiError(error) || 'Failed to update credits');
  }

  const getOriginalList = (typesArg, roleArg) => {
    const accessRole = roleArg || activeUserAccessRole;
    const types = typesArg || selectedTypes;
    if (!accessRole) {
      setLoading(false);
      return;
    }
    if (!types.length) {
      toast.error('Select at least one user type.');
      return;
    }
    setLoading(true);
    const requestId = ++listRequestRef.current;
    getApiCall(API_USERS_LIST, { role: accessRole, roles: buildRolesParam(types) })
      .then((resp) => {
        if (requestId !== listRequestRef.current) return; 
        if (!Array.isArray(resp)) {
          setUserlist([]);
          setOriginalUserlist([]);
          toast.error('User list response was invalid');
          return;
        }
        const filteredList = resp.filter((val) => val.role !== 'role_sadmin');
        const updatedList = filteredList.map((val, idx) => ({
          ...val,
          key: idx + 1,
          company_name: val.assignedCompany?.name || val.companies?.name || null,
          created_at: new Date(val.created_at).toGMTString(),
        }));
        setUserlist(updatedList);
        setOriginalUserlist(updatedList);
      })
      .catch((err) => {
        if (requestId === listRequestRef.current) onError(err);
      })
      .finally(() => {
        if (requestId === listRequestRef.current) setLoading(false);
      });
  };

  const clearFilters = () => {
    setSelectedRole(null);
    setSelectedUsername(null);
    setSearchValue('');
    setUserlist(originalUserlist);
    setNoDataMessage('');
  };

  function matchesRoleFilter(user, role) {
    if (!role) return true;
    if (role === 'role_ccagent_regular') {
      return user.role === USER_ROLES.CALL_CENTRE_AGENT && user.agent?.access !== 'team_lead';
    }
    if (role === 'role_ccagent_team_lead') {
      return user.role === USER_ROLES.CALL_CENTRE_AGENT && user.agent?.access === 'team_lead';
    }
    return user.role === role;
  }

  // Build filter dropdowns from original list (verbatim v1 lines 349-384)
  useEffect(() => {
    const initialUniqueUsernames = [
      ...new Set(
        originalUserlist
          .filter((user) => user.role !== USER_ROLES.CALL_CENTRE_TEAM_LEAD)
          .map((user) => user.username),
      ),
    ].map((username) => ({ label: username, value: username }));

    const initialUniqueRoles = [];
    const seenRoles = new Set();
    for (const user of originalUserlist) {
      if (user.role === USER_ROLES.CALL_CENTRE_AGENT || user.role === USER_ROLES.CALL_CENTRE_TEAM_LEAD) continue;
      if (!seenRoles.has(user.role)) {
        seenRoles.add(user.role);
        initialUniqueRoles.push({ label: USER_ROLES_MAP_PROFILE[user.role] || user.role, value: user.role });
      }
    }
    const ccAgents = originalUserlist.filter((u) => u.role === USER_ROLES.CALL_CENTRE_AGENT);
    if (ccAgents.some((u) => u.agent?.access !== 'team_lead')) {
      initialUniqueRoles.push({ label: 'Agent', value: 'role_ccagent_regular' });
    }
    if (ccAgents.some((u) => u.agent?.access === 'team_lead')) {
      initialUniqueRoles.push({ label: 'Team Lead', value: 'role_ccagent_team_lead' });
    }

    setUniqueRoles(initialUniqueRoles);
    if (initialUniqueUsernames) {
      setUniqueUsernames(initialUniqueUsernames);
      setDefaultUniqueUsernames(initialUniqueUsernames);
    }
  }, [originalUserlist]);

  // Filter usernames when role changes (verbatim v1 lines 386-415)
  useEffect(() => {
    if (selectedRole) {
      const filteredUsernames = originalUserlist
        .filter((user) => matchesRoleFilter(user, selectedRole))
        .map((user) => user.username);
      const uniqueFilteredUsernames = [...new Set(filteredUsernames)].map((username) => ({
        label: username,
        value: username,
      }));
      const isSelectedUsernamePresentAccordingToSelectedRole = uniqueFilteredUsernames.some(
        (user) => user.label === selectedUsername,
      );
      if (selectedUsername) {
        if (!isSelectedUsernamePresentAccordingToSelectedRole) {
          setSelectedUsername(null);
          setUniqueUsernames(defaultUniqueUsernames);
        } else {
          setUniqueUsernames(uniqueFilteredUsernames);
        }
      } else {
        setUniqueUsernames(uniqueFilteredUsernames);
      }
    } else {
      setUniqueUsernames(defaultUniqueUsernames);
    }
  }, [selectedRole, selectedUsername]);

  // User + ACL init from Storage (verbatim v1 lines 417-441)
  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUserId(user.id);
      if (user.role === USER_ROLES.SUPERADMIN) {
        setSuperadminId(user.id);
        checkIsSuperAdmin(true);
      }
      if (user.role === USER_ROLES.CALL_CENTRE_ADMIN) {
        setCallCenterAdmin(true);
      }
      if (user.role === USER_ROLES.RESELLER) {
        setIsReseller(true);
      }
      if (user.role === USER_ROLES.COMPANY_ADMIN) {
        setIsCompanyUser(true);
      }
      if (user?.acl_settings) {
        setUserRole(user?.role);
        setModuleOperations(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  // Refresh ACL on window event (verbatim v1 lines 443-460)
  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setUserRole(user?.role);
          setModuleOperations(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  // Compound filter effect (verbatim v1 lines 462-495)
  useEffect(() => {
    if (selectedRole && !selectedUsername && !searchValue.trim()) {
      setUserlist(originalUserlist.filter((user) => matchesRoleFilter(user, selectedRole)));
    } else if (!selectedRole && selectedUsername && !searchValue.trim()) {
      setUserlist(originalUserlist.filter((user) => user.username === selectedUsername));
    } else if (selectedRole && selectedUsername && !searchValue.trim()) {
      setUserlist(
        originalUserlist.filter(
          (user) => matchesRoleFilter(user, selectedRole) && user.username === selectedUsername,
        ),
      );
    } else if (selectedRole && searchValue.trim()) {
      setUserlist(
        originalUserlist.filter(
          (user) =>
            matchesRoleFilter(user, selectedRole) &&
            (user.username.toLowerCase().includes(searchValue.trim().toLowerCase()) ||
              user.name.toLowerCase().includes(searchValue.trim().toLowerCase()) ||
              user.email?.toLowerCase().includes(searchValue.trim().toLowerCase())),
        ),
      );
    } else if (searchValue && !selectedRole) {
      setUserlist(
        originalUserlist.filter(
          (user) =>
            user.username.toLowerCase().includes(searchValue.trim().toLowerCase()) ||
            user.name.toLowerCase().includes(searchValue.trim().toLowerCase()) ||
            user.email?.toLowerCase().includes(searchValue.trim().toLowerCase()),
        ),
      );
    } else {
      setUserlist(originalUserlist);
    }
  }, [selectedRole, selectedUsername, originalUserlist, searchValue]);

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'users-list',
        data: userlist,
        columns: [
          { header: '#', accessor: 'key' },
          { header: 'Username', accessor: (row) => row.username || '-' },
          { header: 'Name', accessor: (row) => row.name || '-' },
          { header: 'Email', accessor: (row) => row.email || '-' },
          {
            header: 'Role',
            accessor: (row) => USER_ROLES_MAP_PROFILE?.[row.role] || row.role || '-',
          },
          { header: 'Account Type', accessor: (row) => accountTypeLabel(row) || '-' },
          { header: 'Parent', accessor: (row) => row.created_by?.username || '-' },
          {
            header: 'Status',
            accessor: (row) =>
              Number(row.status) === 1 || row.status === 'Active' ? 'Active' : 'Blocked',
          },
          { header: 'Expiry Date', accessor: (row) => formatDateTime(row.expiry_date) },
          ...(!isCallCenterAdmin
            ? [{ header: 'Company', accessor: (row) => formatCompanyCell(row) }]
            : []),
          { header: 'Call Center', accessor: (row) => row.assignedCallCenter?.name || '-' },
          { header: 'Joining Date', accessor: (row) => formatDateTime(row.created_at) },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCheckboxToggle = (userId) => {
    const isSelected = selectedUsers.includes(userId);
    setSelectedUsers(isSelected ? selectedUsers.filter((id) => id !== userId) : [...selectedUsers, userId]);
  };

  const isDemoUserRow = (u) =>
    u?.assignedCompany?.is_demo_company === true || u?.assigned_company_details?.is_demo_company === true;

  const handleBulkUserLogout = () => {
    if (!isSuperAdmin || selectedUsers.length === 0) return;
    const userIds = selectedUsers.filter((id) => Number(id) !== Number(loggedInUserId));
    if (userIds.length === 0) {
      toast.error('Cannot force logout yourself. Select other users.');
      setShowBulkLogoutModal(false);
      return;
    }
    postApiCall(API_USER_BULK_LOGOUT, { userIds })
      .then((data) => {
        const skipped = data?.skipped || [];
        const loggedOut = data?.loggedOutUserIds || [];
        if (loggedOut.length) {
          toast.success(`${loggedOut.length} user(s) logged out successfully`);
        }
        if (skipped.length) {
          toast.warning(
            `Skipped ${skipped.length}: ${skipped
              .slice(0, 3)
              .map((s) => `${s.id} (${s.reason})`)
              .join('; ')}${skipped.length > 3 ? '…' : ''}`,
          );
        }
        getOriginalList();
        setSelectedUsers([]);
        setShowBulkLogoutModal(false);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Bulk logout failed');
        setShowBulkLogoutModal(false);
      });
  };

  const handleBulkStatus = async () => {
    if (bulkStatusTarget == null || bulkStatusLoading || selectedUsers.length === 0) return;
    setBulkStatusLoading(true);
    const ok = await bulkSetUserStatus(
      selectedUsers.map(Number),
      bulkStatusTarget,
      'user',
      (id) => userlist.find((u) => Number(u.id) === id)?.username,
    );
    setBulkStatusLoading(false);
    setBulkStatusTarget(null);
    if (ok) {
      getOriginalList();
      setSelectedUsers([]);
    }
  };

  // superadmin check via Storage (verbatim v1 lines 607-632)
  useEffect(() => {
    async function superadminCheck() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User not found in Storage');
        return null;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in user from Storage', e);
        return null;
      }
      setLoggedInUserId(user.id);
      if (user.role === USER_ROLES.SUPERADMIN) checkIsSuperAdmin(true);
      if (user.role === USER_ROLES.CALL_CENTRE_ADMIN) setCallCenterAdmin(true);
    }
    superadminCheck();
  }, []);

  // Initial user list load (verbatim v1 lines 634-669)
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setLoading(true);
      try {
        const roleLabels = getUserReadAccessRoleLabel();
        const role = roleLabels?.[0]?.value;
        if (!role) {
          toast.error('Unable to load users: no accessible roles found.');
          return;
        }
        setActiveUserAccessRole(role);
        const { defaults: baseDefaults, options } = getUserTypeFilterSetup(getUser()?.role);
        // Prefer the restored (persisted) type selection when it is still valid.
        const restoredTypes = (selectedTypes || []).filter((t) => options.some((o) => o.value === t));
        const defaults = restoredTypes.length ? restoredTypes : baseDefaults;
        setTypeOptions(options);
        setSelectedTypes(defaults);
        const resp = await getApiCall(API_USERS_LIST, {
          role,
          roles: buildRolesParam(defaults),
        });
        if (!cancelled) onUserListSuccess(resp);
      } catch (error) {
        if (!cancelled) onError(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  function onUserListSuccess(resp) {
    if (!Array.isArray(resp)) {
      setUserlist([]);
      setOriginalUserlist([]);
      toast.error('User list response was invalid');
      return;
    }
    const filteredList = resp.filter((val) => val.role !== 'role_sadmin');
    const updatedList = filteredList.map((val, idx) => {
      val['key'] = idx + 1;
      val['company_name'] = val.assignedCompany?.name || val.companies?.name || null;
      val['created_at'] = new Date(val['created_at']).toGMTString();
      return val;
    });
    setUserlist(updatedList);
    setOriginalUserlist(updatedList);
  }

  async function handleDeleteUser(id) {
    const target = userlist.find((u) => u.id === id);
    if (isDemoUserRow(target)) {
      toast.warning('Users of a demo company cannot be deleted.');
      return;
    }
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteUserId(id);
  }

  async function handleAddCredits(id) {
    setShowAddCreditModel(true);
    setAddCreditsId(id);
  }
  async function handleRemoveCredits(id) {
    setShowRemoveCreditModel(true);
    setRemoveCreditsId(id);
  }
  async function handleAddTtsCredits(id) {
    setAddTtsCreditsId(id);
    setAddTtsCreditsValue(0);
    const user = getUser();
    setGiverAvailableTtsCredits(Number(user?.assignedCompany?.tts_credits ?? 0));
    setShowAddTtsCreditModel(true);
  }
  async function handleRemoveTtsCredits(id) {
    setShowRemoveTtsCreditModel(true);
    setRemoveTtsCreditsId(id);
  }

  async function handleInactiveUser(id) {
    setEnableInactiveModal(true);
    setInactiveUserId(id);
  }
  async function handleActiveUser(id) {
    const payload = { status: 1 };
    patchApiCallWithHeader(`${API_USER_STATUS_UPDATE}/${id}`, payload)
      .then((resp) => onUserStatusUpdateSuccess(id, 1, resp))
      .catch(onUserStatusUpdateFail);
  }

  function getDescendantUserIds(rootId, users) {
    const descendants = new Set();
    const queue = [String(rootId)];
    while (queue.length) {
      const currentId = queue.shift();
      users.forEach((u) => {
        const parentId = u.created_by?.id ?? u.created_by;
        if (parentId != null && String(parentId) === currentId) {
          const childId = String(u.id);
          if (!descendants.has(childId)) {
            descendants.add(childId);
            queue.push(childId);
          }
        }
      });
    }
    return descendants;
  }

  function applyUserStatusLocally(userId, newStatus, affectedUserIds = null) {
    const applyToLists = (prev) => {
      const affectedSet = affectedUserIds?.length
        ? new Set(affectedUserIds.map(String))
        : new Set([String(userId), ...getDescendantUserIds(userId, prev)]);
      return prev.map((u) => (affectedSet.has(String(u.id)) ? { ...u, status: newStatus } : u));
    };
    setOriginalUserlist(applyToLists);
    setUserlist(applyToLists);
  }

  async function handleShowPassword(id) {
    setEnableShowPasswordModal(true);
    setShowPasswordId(id);
  }

  const handleSelectAll = () => {
    if (selectedUsers.length === userlist.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(userlist.map((u) => u.id));
    }
  };

  // Delete commit (verbatim v1 lines 788-801)
  useEffect(() => {
    if (confirmDelete) {
      setUserLoadingDelete(true);
      if (isMultiDelete) {
        postApiCall(API_USER_DELETE, { userIds: selectedUsers })
          .then(onUserDeleteSuccess)
          .catch(onUserDeleteFail);
      } else {
        postApiCall(API_USER_DELETE, { userIds: [deleteUserId] })
          .then(onUserDeleteSuccess)
          .catch(onUserDeleteFail);
      }
    }
  }, [confirmDelete]);

  useEffect(() => {
    if (confirmUserInactive) {
      const userId = inactiveUserId;
      const payload = { status: 0 };
      patchApiCallWithHeader(`${API_USER_STATUS_UPDATE}/${userId}`, payload)
        .then((resp) => onUserStatusUpdateSuccess(userId, 0, resp))
        .catch(onUserStatusUpdateFail);
    }
  }, [confirmUserInactive]);

  useEffect(() => {
    if (enableShowPasswordModal) {
      const id = showPasswordId;
      getApiCall(`${API_USERS_LIST}/${id}`, { role: activeUserAccessRole })
        .then(onUserShowPassword)
        .catch(onShowPasswordFail);
    }
  }, [enableShowPasswordModal]);

  function onUserShowPassword(resp) {
    if (resp.actual_password) {
      setShowPasswordUser(`Password is : ${resp.actual_password}`);
    } else {
      setShowPasswordUser('Actual Password is not saved for this user');
    }
    setShowPasswordId(undefined);
    setConfirmShowPassword(false);
  }

  function onShowPasswordFail(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(friendlyApiError(err) || 'Unable to update user status');
    }
    setConfirmDelete(false);
  }

  function onUserStatusUpdateSuccess(userId, newStatus, resp) {
    toast.success('User status updated successfully');
    setEnableInactiveModal(false);
    setInactiveUserId(undefined);
    setConfirmUserInactive(false);
    if (userId != null && newStatus != null) {
      const affectedUserIds = resp?.affectedUserIds ?? null;
      applyUserStatusLocally(userId, newStatus, affectedUserIds);
    }
  }

  function onUserDeleteSuccess() {
    setUserLoadingDelete(false);
    toast.success('User deleted successfully');
    let updatedList;
    if (isMultiDelete) {
      updatedList = userlist.filter((val) => !selectedUsers.includes(val.id));
      setSelectedUsers([]);
    } else {
      updatedList = userlist.filter((val) => val.id != deleteUserId);
    }
    setUserlist(updatedList);
    setEnableDeleteModal(false);
    setDeleteUserId(undefined);
    setConfirmDelete(false);
  }

  function onUserStatusUpdateFail(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(friendlyApiError(err) || 'Unable to update user status');
    }
    setConfirmUserInactive(false);
    setEnableInactiveModal(false);
    setInactiveUserId(undefined);
  }

  function onUserDeleteFail(err) {
    setUserLoadingDelete(false);
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(friendlyApiError(err) || 'Unable to delete user');
    }
    setConfirmDelete(false);
  }

  async function switchAccount(id, status, role) {
    if (isSwitchingAccount) return;
    if (Number(status) === 0 || status === 'Inactive' || status === 0) {
      toast.error(`User is blocked`);
      return;
    }
    setIsSwitchingAccount(true);
    try {
      const response = await postApiCall(API_SWITCH_USER, { id });
      await onAccountSwitchSuccess(response);
    } catch (err) {
      onAccountSwitchError(err);
      setIsSwitchingAccount(false);
    }
  }

  async function onAccountSwitchSuccess(response) {
    const userData = response;
    const switchAccountSuccess = await switchUser(userData);
    if (switchAccountSuccess) {
      toast.success(`Account switched successfully`);
      setTimeout(() => {
        window.location.href = ROUTES.DASHBOARD;
      }, 1500);
    }
  }

  function onError(err) {
    if (typeof err === 'string' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
      return;
    }
    console.error(err);
    setUserlist([]);
    setOriginalUserlist([]);
    const msg = typeof err === 'string' ? err : err?.message || 'Failed to load user list';
    if (!msg.includes('TokenExpiredError')) toast.error(friendlyApiError(err) || 'Failed to load user list');
  }

  function onAccountSwitchError(err) {
    toast.error(friendlyApiError(err) || 'Failed to switch account');
  }

  // ── ACL flags ────────────────────────────────────────────────────
  const canRead = hasPermission(moduleOperations, MODULE_ACL.USERS, 'read');
  const canAdd = hasPermission(moduleOperations, MODULE_ACL.USERS, 'add');
  const canEdit = hasPermission(moduleOperations, MODULE_ACL.USERS, 'edit');
  const canDelete = hasPermission(moduleOperations, MODULE_ACL.USERS, 'delete');

  const handleCreateButtonClick = () => navigate(`/${ROUTES.USERS.CREATE}`);

  // ── Column definitions (matches v1's 11 columns) ─────────────────
  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={selectedUsers.length === userlist.length && userlist.length > 0}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedUsers.includes(row.original.id)}
            onCheckedChange={() => handleCheckboxToggle(row.original.id)}
            aria-label="Select row"
          />
        ),
      },
      {
        id: 'user',
        accessorKey: 'username',
        header: 'User',
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex items-center gap-3">
              <UserAvatar name={u?.name} username={u?.username} role={u?.role} size="md" />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">
                  {u?.name || u?.username || '—'}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {u?.username || ''}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'role',
        accessorKey: 'role',
        header: 'Type',
        cell: ({ row }) => {
          const acct = accountTypeLabel(row.original);
          return (
            <div className="flex flex-col items-start gap-1">
              <UserRoleBadge role={row.original?.role} />
              {acct && (
                <span className="text-xs text-muted-foreground">
                  Account: <span className="font-medium text-foreground">{acct}</span>
                </span>
              )}
            </div>
          );
        },
      },
    ];
    if (!isCallCenterAdmin) {
      cols.push({
        id: 'company_name',
        accessorKey: 'company_name',
        header: 'Company',
        cell: ({ row }) => formatCompanyCell(row.original),
      });
    }
    cols.push(
      {
        id: 'parent',
        header: 'Parent',
        cell: ({ row }) => row.original.created_by?.username || '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <UserStatusBadge user={row.original} />,
      },
      {
        accessorKey: 'expiry_date',
        header: 'Expiry',
        cell: ({ row }) => <UserExpiryCell date={row.original.expiry_date} />,
      },
      // "Details" column removed — every field it exposed now lives inside
      // the UserConsole's Overview tab (Settings icon → Overview). The v1
      // BS Modal that this button opened relied on unported user-details-*
      // CSS classes and rendered as unstyled inline text.
      {
        id: 'hierarchy',
        header: 'Hierarchy',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={`View hierarchy for ${row.original.username}`}
            onClick={(e) => {
              e.stopPropagation();
              setHierarchyUser(row.original);
              setShowHierarchyModal(true);
            }}
          >
            <Network className="h-4 w-4" />
          </Button>
        ),
      },
      {
        id: 'switchUser',
        header: 'Switch',
        size: 70,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => {
          const active = Number(row.original.status) === 1 || row.original.status === 'Active';
          return (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title={
                !active
                  ? 'Cannot switch to a blocked user'
                  : isSwitchingAccount
                    ? 'Switching account…'
                    : `Switch to ${row.original.username}`
              }
              disabled={!active || isSwitchingAccount}
              onClick={() =>
                switchAccount(row.original.id, row.original.status, row.original.role)
              }
            >
              <LogIn className="h-4 w-4" />
            </Button>
          );
        },
      },
      {
        id: 'action',
        header: 'Actions',
        size: 80,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <UserRowActions
            user={row.original}
            canRead={canRead}
            canEdit={canEdit}
            canDelete={canDelete}
            isSwitchingAccount={isSwitchingAccount}
            onEdit={() => navigate(`/${ROUTES.USERS.EDIT.replace(':id', row.original.id)}`)}
            onSwitchAccount={() =>
              switchAccount(row.original.id, row.original.status, row.original.role)
            }
            onBlockToggle={() => {
              const active = Number(row.original.status) === 1 || row.original.status === 'Active';
              if (active) handleInactiveUser(row.original.id);
              else handleActiveUser(row.original.id);
            }}
            onShowPassword={() => handleShowPassword(row.original.id)}
            onResetPassword={() =>
              navigate(`/${ROUTES.USERS.RESET_PASSWORD.replace(':id', row.original.id)}`)
            }
            onDelete={() => handleDeleteUser(row.original.id)}
            onViewHierarchy={() => {
              setHierarchyUser(row.original);
              setShowHierarchyModal(true);
            }}
          />
        ),
      },
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers, userlist, isCallCenterAdmin, isSuperAdmin, moduleOperations, isSwitchingAccount, canRead, canEdit, canDelete]);

  if (!canRead) {
    return (
      <FillPage>
        <div className="my-12 text-center text-base">No read permission found in assigned ACL</div>
      </FillPage>
    );
  }

  // Show the shared "Loading" indicator on the initial fetch (before any
  // user data has arrived). Once the list has loaded once we keep the
  // table visible and let the DataTable's own row-skeleton handle
  // subsequent refreshes so the whole page doesn't flicker to blank.
  if (loading && userlist.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      {isSwitchingAccount && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backdropFilter: 'blur(2px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'all',
          }}
          aria-live="polite"
          aria-busy="true"
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '14px',
              padding: '22px 28px',
              minWidth: '280px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
            }}
          >
            <div
              style={{
                width: '28px',
                height: '28px',
                border: '3px solid #e5e7eb',
                borderTopColor: 'rgb(112, 0, 164)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>Switching account...</span>
              <span style={{ fontSize: '12px', marginTop: '2px' }}>
                Please do not refresh or close this tab.
              </span>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        compact
        className="!mb-0 shrink-0"
        title="Users"
        description="All users under your account with their roles, status, and expiry."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {userlist.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={buttonDisable}
              >
                <Download className="h-4 w-4" />
                {buttonDisable ? 'Exporting…' : 'Export'}
              </Button>
            )}
            {canAdd && (
              <Button size="sm" onClick={handleCreateButtonClick}>
                <Plus className="h-4 w-4" />
                Add user
              </Button>
            )}
          </div>
        }
      />

      {/* Users summary strip — Total / Active / Expired / Roles. Derived
          purely from the userlist we already loaded, no extra API call. */}
      <UserStatStrip users={userlist} />

      <DataTable
        tableId="users.list"
        columns={columns}
        data={userlist}
        isLoading={loading}
        emptyTitle="No users"
        emptyDescription="No users match the current filters."
        pageSize={10}
        exportFilename="users-list.csv"
        disableSearch
        disableExport
        initialColumnPinning={{ right: ['switchUser', 'action'] }}
        beforeTable={
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2">
            {canEdit && (
              <>
                <Button size="sm" variant="outline" onClick={() => setBulkStatusTarget(0)} disabled={bulkStatusLoading || selectedUsers.length === 0}>
                  Block ({selectedUsers.length})
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkStatusTarget(1)} disabled={bulkStatusLoading || selectedUsers.length === 0}>
                  Unblock ({selectedUsers.length})
                </Button>
              </>
            )}
            {isSuperAdmin && selectedUsers.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkLogoutModal(true)}
              >
                Bulk Logout ({selectedUsers.length})
              </Button>
            )}
            <UserTypeFilter
              options={typeOptions}
              value={selectedTypes}
              onApply={(next) => {
                setSelectedTypes(next);
                getOriginalList(next);
              }}
            />
            {/* <Select
              value={selectedUsername ?? 'all'}
              onValueChange={(v) => setSelectedUsername(v === 'all' ? null : v)}
            >
              <SelectTrigger className="h-8 w-44">
                <SelectValue placeholder="Username" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All usernames</SelectItem>
                {uniqueUsernames.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select> */}
            <Input
              placeholder="Search by name, username or email…"
              type="text"
              maxLength={100}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="h-8 w-64"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              disabled={!(searchValue.trim() || selectedRole || selectedUsername)}
            >
              Clear
            </Button>
          </div>
        }
      />

      {/* External modals — copied verbatim from v1 */}
      <RenderUserHierarchyModal
        show={showHierarchyModal}
        setShow={setShowHierarchyModal}
        user={hierarchyUser}
      />


      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
        userLoadingDelete={userLoadingDelete}
        clearSelectedData={() => setSelectedUsers([])}
      />

      <ConfirmDialog
        open={showBulkLogoutModal}
        onOpenChange={setShowBulkLogoutModal}
        title="Bulk Logout Users"
        description="Force-logout the selected user(s)? Call centre agents are skipped (use Agents bulk logout). You cannot logout yourself."
        confirmLabel="Logout"
        cancelLabel="Close"
        onConfirm={handleBulkUserLogout}
      />

      <ConfirmDialog
        open={bulkStatusTarget != null}
        onOpenChange={(open) => {
          if (!open && !bulkStatusLoading) setBulkStatusTarget(null);
        }}
        title={bulkStatusTarget === 0 ? 'Block selected users' : 'Unblock selected users'}
        description={
          bulkStatusTarget === 0
            ? `Block ${selectedUsers.length} user(s)? Every user under them is blocked too and their credits are parked. Users already blocked are skipped.`
            : `Unblock ${selectedUsers.length} user(s)? Every user under them is unblocked too and parked credits are restored. Users already active are skipped.`
        }
        confirmLabel={bulkStatusTarget === 0 ? 'Block' : 'Unblock'}
        cancelLabel="Close"
        loading={bulkStatusLoading}
        onConfirm={handleBulkStatus}
      />

      <RenderInactiveUserModal
        show={enableInactiveModal}
        setShow={setEnableInactiveModal}
        setConfirmUserInactive={setConfirmUserInactive}
        clearSelectedData={() => setSelectedUsers([])}
      />

      <RenderShowPasswordModal
        show={enableShowPasswordModal}
        setShow={setEnableShowPasswordModal}
        setConfirmShowPassword={setConfirmShowPassword}
        passwordValue={showPasswordUser}
        userId={showPasswordId}
        onPasswordReset={() => {
          if (showPasswordId) {
            getApiCall(`${API_USERS_LIST}/${showPasswordId}`, { role: activeUserAccessRole })
              .then(onUserShowPassword)
              .catch(onShowPasswordFail);
          }
        }}
      />

      {/* Call Strategies dialog */}
      <Dialog
        open={showStrategiesModal}
        onOpenChange={(next) => {
          if (!next) {
            setShowStrategiesModal(false);
            setUserCallStrategies(null);
            setUserCallStrategiesUserName(null);
          } else {
            setShowStrategiesModal(true);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Call Strategies</DialogTitle>
            <DialogDescription>
              Username: <span className="font-medium text-foreground">{userCallStrategiesUserName}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">{userCallStratgies}</div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowStrategiesModal(false);
                setUserCallStrategiesUserName(null);
                setUserCallStrategies(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
